import React, { useEffect } from "react";
import {
  Autocomplete,
  Button,
  DataGrid,
  DialogActions,
  esES,
  Grid,
  GridColumns,
  IconButton,
  List,
  SaveButton,
  Stack,
  TextField,
} from "@pankod/refine-mui";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  useAutocomplete,
} from "@pankod/refine-mui";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { IEmployee } from "interfaces";
import {
  IFinishedPieceModalProps,
  IuseFinisedPieces,
  IuseRealWeight,
} from "./interfaces";
import { useFinisedPieces } from "./hooks/useFinisedPieces";
import { useDelete, useList, useUpdate } from "@pankod/refine-core";
import { v4 as uuid } from "uuid";
import _ from "lodash-es";
import { useRealWeight } from "./hooks/useRealWeight";
import { getTodayDate } from "functions";
import { isUnsavedFinishedPiece } from "./applyFinishedPiecesChange";
import { useIsReadOnly } from "hooks/useIsReadOnly";

export const FinishedPiecesModal: React.FC<IFinishedPieceModalProps> = ({
  visible,
  close,
  title,
  data,
  setData,
  history,
}) => {
  const { isReadOnly } = useIsReadOnly();
  const {
    saveButtonProps,
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    refineCoreProps: {
      resource: "null",
    },
  });

  const [editingRecord, setEditingRecord] = React.useState<any>(null);
  const [currentQty, setCurrentQty] = React.useState<number>(
    data?.currentQty || 0
  );
  const [currentWeight, setCurrentWeight] = React.useState<number>(
    data?.realWeight || 0
  );

  const {
    data: { data: historyData } = { data: [] },
    isLoading,
    refetch,
  } = useList<any>({
    resource: history?.resource || "finished-pieces",
    queryOptions: {
      enabled: visible && !!history,
    },
    config: history?.config,
    metaData: history?.metaData,
  });

  const { mutateAsync: updatePiece } = useUpdate();
  const { mutateAsync: deletePiece } = useDelete();

  const { autocompleteProps: employeeAutocompleteProps } =
    useAutocomplete<IEmployee>({
      resource: "employees",
      filters: [{ field: "active", operator: "eq", value: true }],
      defaultValue: data?.currentEmployee?.id,
      onSearch: (search) => [
        {
          field: "name",
          operator: "contains",
          value: search,
        },
      ],
    });

  const previousQty = editingRecord ? +Number(editingRecord.qty || 0) : 0;
  const previousWeight = editingRecord
    ? +Number(editingRecord.real_weight || 0)
    : 0;

  const cleanInputs = () => {
    reset();
    setEditingRecord(null);
    setValue("date", getTodayDate());
    setValue("employee", data?.currentEmployee);
    setValue("qty", "0");
    setValue("real_weight", "0");
  };

  const { orderQty, totalQty } = useFinisedPieces({
    orderQty: data?.orderQty,
    currentQty,
    newQty: watch("qty") ? +watch("qty") : 0,
    previousQty,
  }) as IuseFinisedPieces;

  const { totalRealWeight } = useRealWeight({
    currentRealWeight: currentWeight,
    newRealWeight: watch("real_weight") ? +watch("real_weight") : 0,
    previousRealWeight: previousWeight,
  }) as IuseRealWeight;

  const persistSavedRecord = async (action: "update" | "delete", payload: any) => {
    if (isUnsavedFinishedPiece(payload.id)) {
      return;
    }

    if (action === "update") {
      await updatePiece({
        resource: "finished-pieces",
        id: payload.id,
        values: {
          date: payload.date,
          employee: payload.employee,
          qty: payload.qty,
          real_weight: payload.real_weight,
        },
        successNotification: false,
      });
    } else {
      await deletePiece({
        resource: "finished-pieces",
        id: payload.id,
        successNotification: false,
      });
    }
  };

  const onSubmit = async (submitData: any) => {
    const qty = +Number(submitData.qty).toFixed(0);
    const realWeight = +Number(submitData.real_weight || 0).toFixed(2);
    const action = editingRecord ? "update" : "create";
    const newData = {
      ...submitData,
      id: editingRecord?.id,
      qty,
      total_qty: totalQty,
      real_weight: realWeight,
      total_real_weight: totalRealWeight,
      action,
      previous_qty: previousQty,
      previous_real_weight: previousWeight,
    };

    if (action === "update") {
      await persistSavedRecord("update", newData);
      await refetch?.();
    }

    setCurrentQty(totalQty);
    setCurrentWeight(totalRealWeight || 0);
    setData(newData);
    cleanInputs();
  };

  const handleEdit = (row: any) => {
    setEditingRecord(row);
    setValue("date", (row.date || row.createdAt || getTodayDate()).slice(0, 10));
    setValue("employee", row.employee);
    setValue("qty", String(row.qty ?? 0));
    setValue("real_weight", String(row.real_weight ?? 0));
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm("¿Eliminar este registro de piezas terminadas?")) {
      return;
    }

    const qty = +Number(row.qty || 0).toFixed(0);
    const realWeight = +Number(row.real_weight || 0).toFixed(2);
    const nextQty = currentQty - qty;
    const nextWeight = +(currentWeight - realWeight).toFixed(2);
    const newData = {
      ...row,
      action: "delete",
      qty,
      real_weight: realWeight,
      total_qty: nextQty,
      total_real_weight: nextWeight,
      previous_qty: qty,
      previous_real_weight: realWeight,
    };

    await persistSavedRecord("delete", newData);
    await refetch?.();
    setCurrentQty(nextQty);
    setCurrentWeight(nextWeight);
    setData(newData);
    if (editingRecord && String(editingRecord.id) === String(row.id)) {
      cleanInputs();
    }
  };

  useEffect(() => {
    if (visible) {
      setEditingRecord(null);
      setCurrentQty(data?.currentQty || 0);
      setCurrentWeight(data?.realWeight || 0);
      setValue("employee", data?.currentEmployee);
      setValue("date", getTodayDate());
      setValue("qty", "0");
      setValue("real_weight", "0");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, visible]);

  const extraData = (history?.extraData || []).map((item: any) => {
    return {
      id: item.id || uuid(),
      date: item.date,
      createdAt: item.createdAt || item.date,
      employee: item.employee,
      process: item.process,
      qty: +item.qty,
      real_weight: +item.real_weight,
    };
  });

  const historyWithStatus = [...(historyData || []), ...extraData].map((e) => ({
    ...e,
    createdAt: e.createdAt || e.date,
    status: !_.includes(e?.id?.toString(), "-"),
    onEdit: handleEdit,
    onDelete: handleDelete,
  }));

  const historyColumns: GridColumns = React.useMemo(() => {
    const base = (history?.columns || []).filter(
      (column: any) => column.field !== "actions"
    );

    if (isReadOnly) {
      return base;
    }

    return [
      ...base,
      {
        headerName: "Acciones",
        headerAlign: "center" as const,
        field: "actions",
        align: "center" as const,
        minWidth: 110,
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: function render({ row }: any) {
          return (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                title="Editar"
                onClick={(e) => {
                  e.stopPropagation();
                  row.onEdit?.(row);
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Eliminar"
                onClick={(e) => {
                  e.stopPropagation();
                  row.onDelete?.(row);
                }}
              >
                <DeleteOutline fontSize="small" color="error" />
              </IconButton>
            </Stack>
          );
        },
      },
    ];
  }, [history?.columns, isReadOnly]);

  return (
    <Dialog open={visible} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>
        {title}
        {editingRecord ? " — Editar registro" : ""}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Grid container spacing={2}>
            {!isReadOnly && (
            <>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register("date", {
                  required: "El campo fecha es obligatorio.",
                })}
                error={!!errors?.date}
                helperText={errors.date?.message?.toString()}
                margin="normal"
                fullWidth
                type={"date"}
                id="date"
                label="Fecha"
                name="date"
                defaultValue={getTodayDate()}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="employee"
                rules={{ required: "El campo empleado es obligatorio." }}
                defaultValue={null as any}
                render={({ field }) => {
                  return (
                    <Autocomplete
                      {...employeeAutocompleteProps}
                      {...field}
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      getOptionLabel={(item) => {
                        return (
                          employeeAutocompleteProps?.options?.find(
                            (p) => p?.id?.toString() === item?.id?.toString()
                          )?.name ?? ""
                        );
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return (
                          value === undefined ||
                          option.id.toString() === value.id.toString()
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Empleado"
                          margin="normal"
                          variant="outlined"
                          error={!!errors.employee}
                          helperText={errors.employee?.message?.toString()}
                          required
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register("qty", {
                  required: "El campo cantidad es obligatorio.",
                })}
                error={!!errors?.qty}
                helperText={errors.qty?.message?.toString()}
                margin="normal"
                fullWidth
                id="qty"
                label="Cantidad"
                name="qty"
                type={"number"}
                required
                defaultValue={""}
              />
              <div>
                {orderQty} / {currentQty} / {totalQty}
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              {data?.realWeight !== undefined && (
                <>
                  <TextField
                    {...register("real_weight")}
                    error={!!errors?.real_weight}
                    helperText={errors.real_weight?.message?.toString()}
                    margin="normal"
                    fullWidth
                    id="real_weight"
                    label="Peso real"
                    name="real_weight"
                    type={"number"}
                    defaultValue={""}
                  />
                  <div>
                    {currentWeight} / {totalRealWeight}
                  </div>
                </>
              )}
            </Grid>
            <Grid container justifyContent="flex-end" marginTop={2} gap={1}>
              {editingRecord && (
                <Button type="button" onClick={cleanInputs}>
                  Cancelar edición
                </Button>
              )}
              <SaveButton
                {...saveButtonProps}
                onClick={(e: React.BaseSyntheticEvent) => {
                  e.preventDefault();
                  handleSubmit(
                    (formData) => onSubmit(formData),
                    () => false
                  )(e);
                }}
              />
            </Grid>
            </>
            )}
            <Grid item xs={12}>
              <List breadcrumb={false} title={"Historial"} canCreate={false}>
                <DataGrid
                  rows={historyWithStatus}
                  autoHeight
                  localeText={
                    esES.components.MuiDataGrid.defaultProps.localeText
                  }
                  pageSize={10}
                  columns={historyColumns}
                  loading={isLoading}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textOverflow: "clip",
                      whiteSpace: "break-spaces",
                      lineHeight: 1,
                    },
                  }}
                />
              </List>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
